import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  LayoutDashboard,
  FileText,
  LogOut,
  RefreshCw,
  Loader2,
  Check,
  X,
  Zap,
  Clock,
  Sun,
  Moon,
  Users,
  User,
  Menu,
  Send,
  ChevronDown,
  ChevronUp,
  Hash,
  Building,
  Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Sidebar({ user, isAdmin, theme, toggleTheme, handleLogout, isMobile, onClose }) {
  const location = useLocation();
  
  const NavLink = ({ to, icon: Icon, children, active }) => (
    <Link
      to={to}
      onClick={onClose}
      className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
        active ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
      }`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{children}</span>
    </Link>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div>
          <h1 className="font-heading text-lg font-bold tracking-tight text-primary">ETELEKOM SMS</h1>
          <p className="text-xs text-muted-foreground">Mesaj Yönetim Paneli</p>
        </div>
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-1">
        <NavLink to="/dashboard" icon={LayoutDashboard} active={location.pathname === '/dashboard'}>
          Dashboard
        </NavLink>
        {isAdmin && (
          <NavLink to="/admin" icon={Users} active={location.pathname === '/admin'}>
            Kullanıcı Yönetimi
          </NavLink>
        )}
        <NavLink to="/logs" icon={FileText} active={location.pathname === '/logs'}>
          İşlem Logları
        </NavLink>
      </nav>

      <div className="p-3 border-t border-border space-y-3">
        <div className="flex items-center justify-between p-2">
          <span className="text-sm">Tema</span>
          <Button variant="outline" size="icon" onClick={toggleTheme} className="h-8 w-8">
            {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{user?.username}</span>
            <Badge variant="outline" className="text-xs">
              {user?.role === 'admin' ? 'Admin' : 'User'}
            </Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="h-8 w-8 p-0">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Approval Report Card
function ApprovalReportCard({ log }) {
  const [expanded, setExpanded] = useState(false);
  const result = log.approval_result;
  
  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Card className="bg-card/80 overflow-hidden" data-testid={`approval-report-${log.id}`}>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className="bg-green-500/20 text-green-600 dark:text-green-400">
                <Check className="h-3 w-3 mr-1" />
                Onay
              </Badge>
              <Badge variant="outline" className="font-medium">
                <Tag className="h-3 w-3 mr-1" />
                {log.message_baslik}
              </Badge>
              {log.message_firma && (
                <Badge variant="outline" className="text-muted-foreground">
                  <Building className="h-3 w-3 mr-1" />
                  {log.message_firma}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                {log.user}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {formatTimestamp(log.timestamp)}
              </span>
              {result?.approved_count > 0 && (
                <span className="flex items-center gap-1 font-mono">
                  <Send className="h-3.5 w-3.5" />
                  {result.approved_count} mesaj
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3 shrink-0">
            {result?.toplam_gonderilen > 0 && (
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Toplam Gönderilen</p>
                <p className="text-lg font-bold font-mono text-green-500">
                  {result.toplam_gonderilen.toLocaleString('tr-TR')}
                </p>
              </div>
            )}
            {result?.batch_details?.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="h-8 px-2"
              >
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded batch details */}
      {expanded && result?.batch_details?.length > 0 && (
        <div className="border-t border-border p-4 bg-muted/30">
          <p className="text-xs font-medium text-muted-foreground mb-2">Gönderim Detayları</p>
          <ScrollArea className="max-h-40">
            <div className="space-y-1 font-mono text-sm">
              {result.batch_details.map((line, i) => (
                <p key={i} className={line.toLowerCase().includes('toplam') ? 'font-bold text-green-500' : 'text-muted-foreground'}>
                  {line}
                </p>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </Card>
  );
}

export default function LogsPage() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('reports');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/logs?limit=200`, { headers: getAuthHeader() });
      setLogs(response.data);
    } catch (error) {
      toast.error('Loglar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return <User className="h-4 w-4 text-blue-500" />;
      case 'auto_fetch':
      case 'fetch': return <RefreshCw className="h-4 w-4 text-purple-500" />;
      case 'approve': return <Check className="h-4 w-4 text-green-500" />;
      case 'reject': return <X className="h-4 w-4 text-red-500" />;
      default: return <Zap className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActionBadge = (action) => {
    const labels = {
      login: 'Giriş', auto_fetch: 'Otomatik', fetch: 'Manuel',
      approve: 'Onay', reject: 'Red', user_create: 'Kullanıcı+',
      user_update: 'Güncelle', baslik_create: 'Başlık+', baslik_delete: 'Başlık-'
    };
    const colors = {
      login: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
      auto_fetch: 'bg-purple-500/20 text-purple-600 dark:text-purple-400',
      fetch: 'bg-purple-500/20 text-purple-600 dark:text-purple-400',
      approve: 'bg-green-500/20 text-green-600 dark:text-green-400',
      reject: 'bg-red-500/20 text-red-600 dark:text-red-400',
      user_create: 'bg-orange-500/20 text-orange-600 dark:text-orange-400',
      user_update: 'bg-orange-500/20 text-orange-600 dark:text-orange-400',
      baslik_create: 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400',
      baslik_delete: 'bg-red-500/20 text-red-600 dark:text-red-400'
    };
    return <Badge className={colors[action] || 'bg-gray-500/20'}>{labels[action] || action}</Badge>;
  };

  // Separate approval logs and other logs
  const approvalLogs = logs.filter(l => l.action === 'approve' && l.status === 'success');
  const otherLogs = logs.filter(l => !(l.action === 'approve' && l.status === 'success'));
  
  const stats = {
    total: logs.length,
    approvals: approvalLogs.length,
    totalSent: approvalLogs.reduce((sum, l) => sum + (l.approval_result?.toplam_gonderilen || 0), 0),
    errors: logs.filter(l => l.status === 'error').length
  };

  const sidebarProps = { user, isAdmin, theme, toggleTheme, handleLogout };

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed left-0 top-0 h-screen w-64 border-r border-border bg-card/80 backdrop-blur-sm z-50">
        <Sidebar {...sidebarProps} isMobile={false} onClose={() => {}} />
      </aside>

      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 border-b border-border bg-card/95 backdrop-blur-sm z-50 flex items-center justify-between px-4">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <Sidebar {...sidebarProps} isMobile={true} onClose={() => setMobileMenuOpen(false)} />
          </SheetContent>
        </Sheet>
        <h1 className="font-heading text-lg font-bold text-primary">İşlem Logları</h1>
        <Button variant="ghost" size="icon" onClick={fetchLogs} disabled={loading}>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
        </Button>
      </header>

      <main className="lg:ml-64 pt-14 lg:pt-0 p-4 lg:p-6">
        <div className="hidden lg:flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl font-semibold">İşlem Logları</h2>
            <p className="text-sm text-muted-foreground">
              {isAdmin ? 'Tüm işlem geçmişi ve onay raporları' : 'İşlem geçmişiniz ve onay raporlarınız'}
            </p>
          </div>
          <Button onClick={fetchLogs} disabled={loading} variant="outline">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Yenile</span>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 lg:mb-6">
          <Card className="bg-card/80">
            <CardContent className="p-3 lg:p-4 text-center">
              <p className="text-xs text-muted-foreground">Toplam Log</p>
              <p className="text-xl lg:text-2xl font-bold font-mono">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/80">
            <CardContent className="p-3 lg:p-4 text-center">
              <p className="text-xs text-muted-foreground">Onay Sayısı</p>
              <p className="text-xl lg:text-2xl font-bold font-mono text-green-500">{stats.approvals}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/80">
            <CardContent className="p-3 lg:p-4 text-center">
              <p className="text-xs text-muted-foreground">Toplam Gönderilen</p>
              <p className="text-xl lg:text-2xl font-bold font-mono text-blue-500">{stats.totalSent.toLocaleString('tr-TR')}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/80">
            <CardContent className="p-3 lg:p-4 text-center">
              <p className="text-xs text-muted-foreground">Hata</p>
              <p className="text-xl lg:text-2xl font-bold font-mono text-red-500">{stats.errors}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs: Onay Raporları / Tüm Loglar */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="reports" data-testid="tab-reports">
              Onay Raporları ({approvalLogs.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all-logs">
              Tüm Loglar ({logs.length})
            </TabsTrigger>
          </TabsList>

          {/* Onay Raporları Tab */}
          <TabsContent value="reports">
            {approvalLogs.length === 0 ? (
              <Card className="bg-card/80">
                <CardContent className="p-8 text-center">
                  <Check className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" />
                  <p className="text-muted-foreground">Henüz onay raporu yok</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="approval-reports-list">
                {approvalLogs.map((log) => (
                  <ApprovalReportCard key={log.id} log={log} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tüm Loglar Tab */}
          <TabsContent value="all">
            <Card className="bg-card/80">
              <CardHeader className="p-4">
                <CardTitle className="font-heading text-base lg:text-lg">İşlem Geçmişi</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[60vh] lg:h-[600px]">
                  {logs.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Henüz işlem kaydı yok</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {logs.map((log) => (
                        <div key={log.id} className="p-3 lg:p-4 hover:bg-muted/50" data-testid={`log-item-${log.id}`}>
                          <div className="flex items-start gap-3">
                            <div className="mt-1">{getActionIcon(log.action)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                {getActionBadge(log.action)}
                                <Badge variant={log.status === 'success' ? 'outline' : 'destructive'} className="text-xs">
                                  {log.status === 'success' ? 'OK' : 'Hata'}
                                </Badge>
                                <span className="text-xs text-muted-foreground">{log.user}</span>
                              </div>
                              <p className="text-sm truncate">{log.details}</p>
                              {log.message_baslik && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {log.message_baslik} {log.message_firma && `- ${log.message_firma}`}
                                </p>
                              )}
                              {log.approval_result?.toplam_gonderilen > 0 && (
                                <p className="text-xs font-mono text-green-500 mt-1">
                                  Toplam gönderilen: {log.approval_result.toplam_gonderilen.toLocaleString('tr-TR')}
                                </p>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTimestamp(log.timestamp)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
